import { SearchIcon } from '@chakra-ui/icons';
import {
  Box,
  Divider,
  Input,
  InputGroup,
  InputLeftElement,
  Skeleton,
  Text,
  VStack,
} from '@chakra-ui/react';
import ChartingNote from '@web-app/components/ChartingNote';
import { ChartingNoteProps } from '@web-app/components/ChartingNote/types';
import { singleDigitDateFormat } from '@web-app/utils/date-time.utils';
import { parseGlp1FollowUpSnapshot } from '@web-app/utils/glp1/follow-up-snapshot';
import { format } from 'date-fns';
import { Document } from 'flexsearch';
import { highlightSearchTerm } from 'highlight-search-term';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { Glp1FollowUpNoteBody } from './Glp1FollowUpNoteBody';

export type ChartNote = ChartingNoteProps['chartingNoteData'];

interface ChartHistoryUIProps {
  chartNotes: ChartNote[];
  isLoading?: boolean;
  isError?: boolean;
}

/**
 * UI component that renders a list of ChartingNote components.
 * Separated from ChartHistory to allow for easier testing and reusability.
 */
export const ChartHistoryUI = memo(function ChartHistoryUI({
  chartNotes,
  isLoading = false,
  isError = false,
}: ChartHistoryUIProps) {
  const [searchQuery, setSearchQuery] = useState('');

  // Function to apply highlighting
  const applyHighlighting = useCallback(() => {
    if (searchQuery.trim()) {
      highlightSearchTerm({
        search: searchQuery,
        selector: '.chart-note-results',
      });
    } else {
      highlightSearchTerm({
        search: '',
        selector: '.chart-note-results',
      });
    }
  }, [searchQuery]);

  // Build Flexsearch Document index with multi-field search
  const { searchIndex, idToNoteMap } = useMemo(() => {
    if (chartNotes.length === 0) {
      return { searchIndex: null, idToNoteMap: new Map<string, ChartNote>() };
    }

    // Filter out undefined notes and ensure we have valid notes with IDs
    const validNotes = chartNotes.filter(
      (note): note is NonNullable<ChartNote> => Boolean(note?.id),
    );

    if (validNotes.length === 0) {
      return { searchIndex: null, idToNoteMap: new Map<string, ChartNote>() };
    }

    const index = new Document<NonNullable<ChartNote>>({
      tokenize: 'forward',
      cache: 100,
      document: {
        id: 'id',
        index: [
          {
            field: 'appointmentDate',
            tokenize: 'exact',
            custom: (doc: NonNullable<ChartNote>) => {
              const dateString = doc?.form_answer_group?.appointment?.date;
              if (!dateString) return '';
              try {
                const date = new Date(dateString);
                return format(date, singleDigitDateFormat);
              } catch {
                return '';
              }
            },
          },
          {
            field: 'formAnswerGroupName',
            custom: (doc: NonNullable<ChartNote>) =>
              doc?.form_answer_group?.name || '',
          },
          {
            field: 'formAnswerLabels',
            custom: (doc: NonNullable<ChartNote>) => {
              const labels: string[] = [];
              if (doc?.form_answer_group?.form_answers) {
                for (const formAnswer of doc.form_answer_group.form_answers) {
                  if (formAnswer?.label) {
                    labels.push(formAnswer.label);
                  }
                }
              }
              return labels.join(' ');
            },
          },
          {
            field: 'formAnswerContent',
            custom: (doc: NonNullable<ChartNote>) => {
              const contents: string[] = [];
              if (doc?.form_answer_group?.form_answers) {
                for (const formAnswer of doc.form_answer_group.form_answers) {
                  if (formAnswer?.answer) {
                    contents.push(formAnswer.answer);
                  }
                }
              }
              return contents.join(' ');
            },
          },
          {
            field: 'appointmentTypeName',
            custom: (doc: NonNullable<ChartNote>) =>
              doc?.form_answer_group?.appointment?.appointment_type?.name || '',
          },
        ],
      },
    });

    // Create a map for efficient lookup by ID
    const idMap = new Map<string, ChartNote>();

    // Add documents to index using their actual id field
    for (const note of validNotes) {
      index.add(note);
      idMap.set(note.id, note);
    }

    return { searchIndex: index, idToNoteMap: idMap };
  }, [chartNotes]);

  // Filter chart notes based on search query
  const filteredChartNotes = useMemo(() => {
    if (!searchQuery.trim() || !searchIndex || searchQuery.length < 3) {
      return chartNotes.filter(
        (note): note is NonNullable<ChartNote> =>
          note !== undefined && note !== null,
      );
    }

    const results = searchIndex.search(searchQuery);
    // Document search returns array of field results: [{ field: "fieldName", result: [id1, id2, ...] }]
    // We need to merge all results and get unique IDs
    const resultIds = new Set<string>(
      results
        .flatMap((fieldResult) =>
          Array.isArray(fieldResult.result)
            ? fieldResult.result
            : [fieldResult.result],
        )
        .filter((id): id is string => typeof id === 'string'),
    );

    return Array.from(resultIds)
      .map((id) => idToNoteMap.get(id))
      .filter(
        (note): note is NonNullable<ChartNote> =>
          note !== undefined && note !== null,
      );
  }, [searchQuery, searchIndex, idToNoteMap, chartNotes]);

  // Highlight search terms in the results
  useEffect(() => {
    applyHighlighting();
  }, [searchQuery, filteredChartNotes, applyHighlighting]);

  // Loading state
  if (isLoading) {
    return (
      <Box borderRadius="md" px={4} py={2} width="100%">
        {Array.from({ length: 3 }, (_, i) => (
          <Skeleton
            key={i}
            height="60px"
            borderRadius="md"
            mb={i < 2 ? '12px' : 0}
            data-testid="chart-note-skeleton"
          />
        ))}
      </Box>
    );
  }

  // Error state
  if (isError) {
    return (
      <Box borderRadius="md" px={4} py={2} width="100%">
        <Text align="center" color="status.error" py={8}>
          Error loading chart notes. Please try again later.
        </Text>
      </Box>
    );
  }

  // Empty state
  if (chartNotes.length === 0) {
    return (
      <Box borderRadius="md" px={4} py={2} width="100%">
        <Text align="center" color="text.subtle" py={8}>
          No chart notes available
        </Text>
      </Box>
    );
  }

  return (
    <VStack spacing={3} align="stretch" width="100%">
      {/* Search Bar */}
      <Box px={4} pt={2}>
        <InputGroup>
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.400" />
          </InputLeftElement>
          <Input
            placeholder="Search chart notes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            backgroundColor="white"
            borderRadius="md"
            borderColor="border.default"
            _focusVisible={{
              ring: 1,
              ringColor: 'primary',
            }}
            data-testid="chart-note-search-input"
          />
        </InputGroup>
      </Box>

      {/* Results */}
      {filteredChartNotes.length === 0 ? (
        <Box borderRadius="md" px={4} py={2} width="100%">
          <Text align="center" color="text.subtle" py={8}>
            No chart notes match your search
          </Text>
        </Box>
      ) : (
        <VStack
          data-testid="chart-note-results"
          className="chart-note-results"
          spacing={2}
          align="stretch"
          overflowY="auto"
          maxHeight="100%"
          divider={<Divider borderColor="border.subtle" />}
          px={4}
        >
          {filteredChartNotes.map((chartNote) => {
            const snapshot = parseGlp1FollowUpSnapshot(
              chartNote.form_answer_group?.metadata,
            );

            return (
              <Box key={chartNote.id}>
                <ChartingNote
                  chartingNoteTitle={chartNote.form_answer_group?.name || ''}
                  chartingNoteData={chartNote}
                  body={
                    snapshot ? (
                      <Glp1FollowUpNoteBody snapshot={snapshot} />
                    ) : undefined
                  }
                  onDidExpand={applyHighlighting}
                />
              </Box>
            );
          })}
        </VStack>
      )}
    </VStack>
  );
});
